import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Button,
  Checkbox,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@/components'
import { cn } from '@/lib/utils'
import type { ClarifyData, ClarifyQuestion, ClarifyResponses } from './clarifyTypes'

// Sentinel value for the "Other" free-text choice added to every
// single/multiple question. Never sent to the server — the typed text is.
const OTHER_ID = '__other__'

// Single-choice answers auto-advance to the next question. "Other" and
// free-text don't, so the user can keep typing.
const AUTO_ADVANCE_MS = 200

/** Markdown intro shared by the overlay and the read-only feed card. */
export function ClarifyIntro({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cn('text-body text-foreground markdown-content break-words', className)}>
      <ReactMarkdown
        components={{
          p: ({ node: _node, ...props }) => (
            <p className="my-2 first:mt-0 last:mb-0" {...props} />
          ),
          strong: ({ node: _node, ...props }) => (
            <strong className="font-semibold text-foreground" {...props} />
          ),
          em: ({ node: _node, ...props }) => <em className="italic" {...props} />,
          code: ({ node: _node, ...props }) => (
            <code className="rounded-sm bg-muted px-1.5 py-0.5 text-mono-sm" {...props} />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
}

interface ClarifyOverlayProps {
  msgId: string
  data: ClarifyData
  onSubmit: (msgId: string, responses: ClarifyResponses) => void
  onDismiss: (msgId: string) => void
}

/**
 * AI question card docked in place of the chat composer — one question at a
 * time. Numbered tabs across the top, chevrons to move back/forth, Submit on
 * the last step, Dismiss to decline the whole form. Answering a single-choice
 * question advances to the next tab automatically.
 */
export default function ClarifyOverlay({
  msgId,
  data,
  onSubmit,
  onDismiss,
}: ClarifyOverlayProps) {
  const questions = data.questions || []

  const [values, setValues] = useState<Record<string, string | string[]>>(() => {
    const seed: Record<string, string | string[]> = {}
    for (const q of questions) seed[q.id] = q.type === 'multiple' ? [] : ''
    return seed
  })
  const [otherText, setOtherText] = useState<Record<string, string>>({})
  const [step, setStep] = useState(0)
  const [invalid, setInvalid] = useState<Set<string>>(new Set())

  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
    }
  }, [])

  const total = questions.length
  const current = questions[step]
  const isLast = step >= total - 1

  const isAnswered = (q: ClarifyQuestion | undefined): boolean => {
    if (!q) return false
    const v = values[q.id]
    const other = (otherText[q.id] ?? '').trim()
    if (q.type === 'multiple') {
      const arr = Array.isArray(v) ? v : []
      const concrete = arr.filter((id) => id !== OTHER_ID)
      return concrete.length > 0 || (arr.includes(OTHER_ID) && other !== '')
    }
    if (q.type === 'single') {
      if (v === OTHER_ID) return other !== ''
      return typeof v === 'string' && v !== ''
    }
    return typeof v === 'string' && v.trim() !== ''
  }

  const clearInvalid = (questionId: string) => {
    setInvalid((prev) => {
      if (!prev.has(questionId)) return prev
      const next = new Set(prev)
      next.delete(questionId)
      return next
    })
  }

  const setValue = (questionId: string, v: string | string[], advance: boolean) => {
    setValues((prev) => ({ ...prev, [questionId]: v }))
    clearInvalid(questionId)
    if (advance && step < total - 1) {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
      advanceTimerRef.current = setTimeout(() => {
        setStep((s) => Math.min(s + 1, total - 1))
        advanceTimerRef.current = null
      }, AUTO_ADVANCE_MS)
    }
  }

  const setOther = (questionId: string, text: string) => {
    setOtherText((prev) => ({ ...prev, [questionId]: text }))
    clearInvalid(questionId)
  }

  const goNext = () => setStep((s) => Math.min(s + 1, total - 1))
  const goPrev = () => setStep((s) => Math.max(s - 1, 0))

  const buildAnswer = (q: ClarifyQuestion): string | null => {
    const v = values[q.id]
    const other = (otherText[q.id] ?? '').trim()

    if (q.type === 'single') {
      if (v === OTHER_ID) return other !== '' ? other : null
      if (typeof v !== 'string' || v === '') return null
      const option = q.options?.find((opt) => opt.id === v)
      return option?.label || String(v)
    }
    if (q.type === 'multiple') {
      const arr = Array.isArray(v) ? v : []
      const labels = (q.options || [])
        .filter((opt) => arr.includes(opt.id))
        .map((opt) => opt.label)
      if (arr.includes(OTHER_ID) && other !== '') labels.push(other)
      return labels.length > 0 ? labels.join(', ') : null
    }
    const text = typeof v === 'string' ? v.trim() : ''
    return text !== '' ? text : null
  }

  const handleSubmit = () => {
    const missing = questions.filter((q) => q.required && !isAnswered(q))
    if (missing.length > 0) {
      setInvalid(new Set(missing.map((q) => q.id)))
      const firstMissing = questions.findIndex((q) => q.id === missing[0].id)
      if (firstMissing >= 0) setStep(firstMissing)
      return
    }

    // Structured responses preserve question + answer pairs for chat history.
    const responses: ClarifyResponses = {}
    for (const q of questions) {
      const answer = buildAnswer(q)
      if (answer !== null) responses[q.id] = { question: q.text, answer }
    }
    onSubmit(msgId, responses)
  }

  if (total === 0) return null

  const intro = data.description || data.title

  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid="clarify-overlay">
      {intro && <ClarifyIntro text={intro} className="mb-3" />}

      {/* Question tabs — one per question, click to jump; answered ones show a tick. */}
      {total > 1 && (
        <div className="flex items-center gap-1.5 mb-3">
          {questions.map((q, i) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setStep(i)}
              aria-label={`Question ${i + 1}`}
              aria-current={i === step}
              className={cn(
                'inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-md text-caption tabular-nums transition-colors cursor-pointer',
                i === step
                  ? 'bg-primary text-primary-foreground'
                  : isAnswered(q)
                    ? 'bg-muted text-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:text-foreground',
                invalid.has(q.id) && i !== step && 'ring-1 ring-danger text-danger',
              )}
            >
              {isAnswered(q) && i !== step ? <Check size={11} strokeWidth={2.5} /> : i + 1}
            </button>
          ))}
          <span className="ml-auto text-caption text-muted-foreground tabular-nums">
            {step + 1} / {total}
          </span>
        </div>
      )}

      {current && (
        <ClarifyStepField
          key={current.id}
          msgId={msgId}
          question={current}
          value={values[current.id]}
          otherText={otherText[current.id] ?? ''}
          showError={invalid.has(current.id)}
          onChange={(v, advance) => setValue(current.id, v, advance)}
          onOtherChange={(text) => setOther(current.id, text)}
        />
      )}

      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={goPrev}
          disabled={step === 0}
          className="inline-flex items-center gap-0.5 text-caption text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
        >
          <ChevronLeft size={14} strokeWidth={2} /> Back
        </button>
        {isLast ? (
          <Button size="sm" variant="default" className="text-label" onClick={handleSubmit}>
            Submit
          </Button>
        ) : (
          <Button size="sm" variant="default" className="text-label gap-1" onClick={goNext}>
            Next <ChevronRight size={13} strokeWidth={2} />
          </Button>
        )}
        <button
          type="button"
          className="ml-auto text-caption text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => onDismiss(msgId)}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

interface ClarifyStepFieldProps {
  msgId: string
  question: ClarifyQuestion
  value: string | string[] | undefined
  otherText: string
  showError: boolean
  onChange: (v: string | string[], advance: boolean) => void
  onOtherChange: (text: string) => void
}

function ClarifyStepField({
  msgId,
  question,
  value,
  otherText,
  showError,
  onChange,
  onOtherChange,
}: ClarifyStepFieldProps) {
  const fieldId = (suffix: string) => `clarify-${msgId}-${question.id}-${suffix}`

  const header = (
    <div className="text-caption text-muted-foreground mb-1">
      {question.text}
      {question.required && <span className="text-danger"> *</span>}
    </div>
  )

  const error = showError && (
    <p className="text-caption text-danger mt-1">This question is required</p>
  )

  const otherField = (
    <Textarea
      value={otherText}
      onChange={(e) => onOtherChange(e.target.value)}
      className="text-body min-h-[40px] mt-2"
      placeholder="Type your answer…"
      autoFocus
    />
  )

  if (question.type === 'single') {
    const isOther = value === OTHER_ID
    return (
      <div>
        {header}
        <RadioGroup
          value={typeof value === 'string' ? value : ''}
          onValueChange={(v) => onChange(v, v !== OTHER_ID)}
        >
          {question.options?.map((opt) => (
            <div key={opt.id} className="flex items-start gap-2">
              <RadioGroupItem value={opt.id} id={fieldId(opt.id)} className="mt-0.5" />
              <Label htmlFor={fieldId(opt.id)} className="text-body font-normal flex-col items-start gap-0.5">
                <span>{opt.label}</span>
                {opt.description && (
                  <span className="text-caption text-muted-foreground">{opt.description}</span>
                )}
              </Label>
            </div>
          ))}
          <div className="flex items-start gap-2">
            <RadioGroupItem value={OTHER_ID} id={fieldId('other')} className="mt-0.5" />
            <Label
              htmlFor={fieldId('other')}
              className="text-body font-normal text-muted-foreground"
            >
              Other
            </Label>
          </div>
        </RadioGroup>
        {isOther && otherField}
        {error}
      </div>
    )
  }

  if (question.type === 'multiple') {
    const arr = Array.isArray(value) ? value : []
    const toggle = (id: string, checked: boolean) =>
      onChange(checked ? [...arr, id] : arr.filter((x) => x !== id), false)
    return (
      <div>
        {header}
        <div className="flex flex-col gap-1.5">
          {question.options?.map((opt) => (
            <div key={opt.id} className="flex items-start gap-2">
              <Checkbox
                id={fieldId(opt.id)}
                checked={arr.includes(opt.id)}
                onCheckedChange={(c) => toggle(opt.id, c === true)}
                className="mt-0.5"
              />
              <Label htmlFor={fieldId(opt.id)} className="text-body font-normal flex-col items-start gap-0.5">
                <span>{opt.label}</span>
                {opt.description && (
                  <span className="text-caption text-muted-foreground">{opt.description}</span>
                )}
              </Label>
            </div>
          ))}
          <div className="flex items-start gap-2">
            <Checkbox
              id={fieldId('other')}
              checked={arr.includes(OTHER_ID)}
              onCheckedChange={(c) => toggle(OTHER_ID, c === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor={fieldId('other')}
              className="text-body font-normal text-muted-foreground"
            >
              Other
            </Label>
          </div>
        </div>
        {arr.includes(OTHER_ID) && otherField}
        {error}
      </div>
    )
  }

  return (
    <div>
      {header}
      <Textarea
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value, false)}
        className="text-body min-h-[48px]"
        placeholder="Type here…"
      />
      {error}
    </div>
  )
}
